/**
 * Builds Pearl product URLs from the current page context.
 *
 * Patient/office IDs are only embedded when the caller sets `onPatientPage`.
 * The shell does not decide what a patient page is.
 */

import type { PearlAction } from './icon-config';

/**
 * Resolves a build-time-injected base URL (esbuild `define`), with a
 * `globalThis` override for tests and a hardcoded prod fallback for any
 * non-bundled context (Jest).
 *
 * @param injected - The value of the esbuild-injected constant (or undefined under Jest).
 * @param globalKey - The `globalThis` key tests may set to override.
 * @param fallback - Prod default when neither source provides a value.
 * @returns The resolved base URL (no trailing slash).
 */
function resolveBase(
  injected: string | undefined,
  globalKey: string,
  fallback: string,
): string {
  if (typeof injected === 'string' && injected) return injected;
  const fromGlobal = (globalThis as Record<string, unknown>)[globalKey];
  if (typeof fromGlobal === 'string' && fromGlobal) return fromGlobal;
  return fallback;
}

/** Login host (popup + auth probe). Dev builds → dev.secondopinion. */
export const LOGIN_BASE = resolveBase(
  process.env.LOGIN_BASE,
  '__LOGIN_BASE__',
  'https://secondopinion.hellopearl.com',
);

/**
 * Second Opinion base ("tooth" action). The login host IS the Second
 * Opinion host, so this follows the env-injected `LOGIN_BASE`.
 */
export const SO_BASE = LOGIN_BASE;

/** PI base — practice / insights / treatments / insurance actions. Dev builds → dev.pi. */
export const PI_BASE = resolveBase(
  process.env.PI_BASE,
  '__PI_BASE__',
  'https://pi.hellopearl.com',
);

/** Precheck base — insurance override for orgs WITHOUT is_pi. Dev builds → dev.precheck. */
export const PRECHECK_BASE = resolveBase(
  process.env.PRECHECK_BASE,
  '__PRECHECK_BASE__',
  'https://precheck.hellopearl.com',
);

export interface BuildUrlContext {
  patientId: string | null;
  officeId: string | null;
  pathname: string;
  /** Full page URL including hash. */
  href?: string;
  /**
   * When true, embed patient/office ids on the patient-details URL.
   * Callers (content wiring, popup compose) supply this; the shell does not
   * inspect the host pathname.
   */
  onPatientPage?: boolean;
  /** When PI is not active but Insurance IS, redirects to precheck.hellopearl.com. */
  insuranceUrlOverride?: string;
  /** Voice URL (env-specific, injected from build config). */
  voiceUrl?: string;
}

/**
 * Returns the Pearl product URL for the given action, or `null` for Voice.
 * Voice is not driven by this URL path — it mounts the embedded iframe
 * via a separate code path.
 *
 * The new submenu actions (`canceled`, `call-sheets`, `production`, `funnels`)
 * route to `${PI_BASE}/?ref=<action>` placeholders until product confirms the
 * real URLs. The visible `?ref=` marker means a wrong landing page is loud
 * rather than silent.
 *
 * @param action - One of the leaf action ids defined in `icon-config.ts`.
 * @param ctx - Patient/office/path context the URL may need to embed.
 * @returns The full URL to open, or `null` if unavailable.
 */
export function buildUrl(
  action: PearlAction,
  ctx: BuildUrlContext,
): string | null {
  if (action === 'perio') return null;
  if (action === 'voice') return ctx.voiceUrl || null;

  const onPatientPage = ctx.onPatientPage === true;
  const pid = onPatientPage ? ctx.patientId : null;
  const oid = onPatientPage ? ctx.officeId : null;

  switch (action) {
    case 'patient': {
      const params = new URLSearchParams();
      if (oid) params.set('initialOfficeId', oid);
      if (pid) params.set('initialPatientId', pid);
      const qs = params.toString();
      return `${PI_BASE}/patient-details${qs ? '?' + qs : ''}`;
    }
    case 'tooth':
      return ctx.patientId
        ? `${SO_BASE}/#/disambiguation/${ctx.patientId}`
        : SO_BASE;
    case 'daily-huddle':
      return `${PI_BASE}/schedule?ref=daily-huddle`;
    case 'patient-report': {
      if (ctx.patientId) {
        const params = new URLSearchParams();
        params.set('pearl_dock', 'patient-report');
        if (ctx.officeId) params.set('office_id', ctx.officeId);
        return `${SO_BASE}/#/disambiguation/${encodeURIComponent(ctx.patientId)}?${params.toString()}`;
      }
      return SO_BASE;
    }
    case 'calendar':
      return `${PI_BASE}/schedule`;
    case 'dashboard':
      // Insights → Dashboard sub-item resolves to the existing /dso page.
      return `${PI_BASE}/dso`;
    case 'treatments':
      return `${PI_BASE}/ai-opportunities-review`;
    case 'insurance':
      return ctx.insuranceUrlOverride || `${PI_BASE}/insurance`;
    case 'canceled':
      return `${PI_BASE}/cancellations`;
    case 'call-sheets':
      return `${PI_BASE}/call-sheet`;
    case 'production':
      return `${PI_BASE}/production`;
    case 'funnels':
      return `${PI_BASE}/dashboard`;
    default:
      // Legacy "insights" key (flat-nav era): treat as Dashboard for back-compat.
      // Removing this branch would break the popup controller's existing tests.
      if ((action as string) === 'insights') return `${PI_BASE}/dso`;
      return PI_BASE;
  }
}
